import { Card, Col, Divider, Flex, message, Row, Tag, Tooltip } from 'antd';
import clsx from 'clsx';
import { map, slice } from 'lodash';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../../../../../../AppContext';
import { CloseIcon, InfoIconDark } from '../../../../../../../assets/svg';
import useLazyQueryWithCallbacks from '../../../../../../../common/CommonUseLazyQuery';
import {
  ACCESS_TYPE,
  API_TO_AGGREGATE_FUNCTION_LABELS,
  BREAKPOINTS,
  DATE_ISO_FORMAT,
  DATE_TIME_WITH_AT,
  DEFAULT_PAGE_SIZE,
  DEFAULTDATEFORMAT,
  PROPERTY_TYPES,
  REGISTER_DETAILS_TYPES,
  REGISTER_LOG_STATUS,
  REGISTER_STATUS_CLASSNAME,
  ROUTES,
  STAGE_STATUS_KEYS,
  TAB_KEYS,
  TIME_12_HOUR_FORMAT,
  TIME_24_HOUR_FORMAT,
} from '../../../../../../../common/constants';
import useRouter from '../../../../../../../common/useRouter';
import {
  removeHistoryStateData,
  titleCase,
  toCamelCase,
} from '../../../../../../../common/utils';
import AggregateDropdown, {
  createAggregateHandlers,
  processRegisterStatsResults,
} from '../../../../../../../components/AggregateDropdown';
import CommonCard from '../../../../../../../components/CommonCard';
import CommonPopover from '../../../../../../../components/CommonPopover';
import CommonTable from '../../../../../../../components/CommonTable';
import CommonTooltip from '../../../../../../../components/CommonTooltip';
import EllipsisText from '../../../../../../../components/EllipsisText';
import HasAccess from '../../../../../../../components/HasAccess';
import InfiniteScrollHandler from '../../../../../../../components/InfiniteScrollHandler';
import TableFilterDropdown from '../../../../../../../components/TableFilterDropdown';
import {
  GET_REGISTER_LOG_DROPDOWN_LIST,
  GET_REGISTER_LOGS,
  REGISTER_STATS,
} from '../../../../../graphql/Queries';
import FieldIcon from '../FieldIcon';

const MAX_EQC_DISPLAY_COUNT = 2;

const NomenclatureItem = ({ text }) => {
  const [isTooltipEnabled, setIsTooltipEnabled] = useState(true);
  const handleCapture = (e) => {
    const { action } = e?.target?.dataset;
    if (action === 'view-more') {
      setIsTooltipEnabled(false);
    } else if (action === 'view-less') {
      setIsTooltipEnabled(true);
    }
  };
  return (
    <Tooltip title={isTooltipEnabled ? text : null} mouseLeaveDelay={0}>
      <div
        key={text}
        className="nomenclature-field"
        onClickCapture={handleCapture}
      >
        <EllipsisText
          className="nomenclature-field-text"
          text={text || '-'}
          heightRows={3}
        />
      </div>
    </Tooltip>
  );
};

const SetsTable = ({
  registerDetailsData,
  registerId,
  showListView,
  setIsDataEmpty,
  globalTeamFilter = [],
  searchValue = '',
  approvalStatus = '',
  selectedAggregates,
  setSelectedAggregates,
  showDeletedEntries = false,
  columnFilters,
  setColumnFilters,
  sortedInfo,
  setSortedInfo,
}) => {
  const {
    navigate,
    params: { projectId },
    location,
  } = useRouter();
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const { getCurrentProjectDetail } = useContext(AppContext);
  const currentProjectDetails = getCurrentProjectDetail();
  const initialLogsFilter = {
    skip: 0,
    limit: DEFAULT_PAGE_SIZE,
    projectAgencyIds:
      globalTeamFilter?.length > 0 ? globalTeamFilter : undefined,
    search: searchValue || null,
    ...(columnFilters && Object?.keys(columnFilters)?.length > 0
      ? { columnFilters }
      : {}),
  };
  const initialPaginationValue = {
    total: 0,
    current: 1,
  };
  const [setsData, setSetsData] = useState([]);
  const [scrollFlag, setScrollFlag] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [registerLogsFilter, setRegisterLogsFilter] =
    useState(initialLogsFilter);
  const [paginationProps, setPaginationProps] = useState(
    initialPaginationValue,
  );
  const [aggregateResults, setAggregateResults] = useState({});
  const registerNavFilter = location?.state?.registerNavFilter;
  const [openApproverPopover, setOpenApproverPopover] = useState(false);
  const [selectedApproverRecord, setSelectedApproverRecord] = useState();

  const hasInspectionAccess = HasAccess({ type: ACCESS_TYPE?.INSPECTION });

  const { handleAggregateChange, handleSelectAll, handleClearAll } =
    createAggregateHandlers(
      selectedAggregates,
      setSelectedAggregates,
      setAggregateResults,
    );

  const initialRegisterStatsFilter = {
    projectAgencyIds:
      globalTeamFilter?.length > 0 ? globalTeamFilter : undefined,
    columnFilters,
  };

  const [registerStatsFilter, setRegisterStatsFilter] = useState(
    initialRegisterStatsFilter,
  );

  const [getRegisterStats] = useLazyQueryWithCallbacks(REGISTER_STATS, {
    fetchPolicy: 'network-only',
    onCompleted(res) {
      processRegisterStatsResults(res, setAggregateResults);
    },
    onError() {},
  });

  const fetchRegisterStats = () => {
    if (Object?.keys(selectedAggregates)?.length > 0) {
      const aggregateData = Object?.entries(selectedAggregates)?.map(
        ([fieldId, operations]) => ({
          id: fieldId,
          operations,
        }),
      );

      getRegisterStats({
        variables: {
          data: {
            fields: aggregateData,
            registerId,
            type: REGISTER_DETAILS_TYPES.DETAIL,
          },
          filter: registerStatsFilter,
        },
      });
    } else {
      setAggregateResults({});
    }
  };

  const [getRegisterLogs, { loading }] = useLazyQueryWithCallbacks(GET_REGISTER_LOGS, {
    fetchPolicy: 'cache-and-network',
    onCompleted(response) {
      const data = response?.registerLogDetailEntryByRegisterList?.data;
      const total = response?.registerLogDetailEntryByRegisterList?.total;
      const pagination = {
        ...paginationProps,
        total,
      };
      setIsDataEmpty(!(total > 0));
      const result = map(data, (item, index) => {
        const obj = Object.keys(item)?.reduce((acc, curr) => {
          const newKey =
            curr === 'createdAt' || curr === 'createdBy'
              ? curr
              : toCamelCase(curr);
          if (curr === 'createdAt' && item[curr]) {
            acc[newKey] = moment(item[curr])?.format(DEFAULTDATEFORMAT);
          } else {
            acc[newKey] = item[curr];
          }
          return acc;
        }, {});
        obj.index = registerLogsFilter.skip + index + 1;
        return obj;
      });

      if (scrollFlag) {
        setSetsData((prevData) => [...prevData, ...result]);
        setScrollFlag(false);
      } else {
        setSetsData(result);
      }
      setHasMore(setsData.length + result.length < total);
      setPaginationProps(pagination);

      if (total > 0) {
        fetchRegisterStats();
      } else if (total === 0 && Object?.keys(selectedAggregates)?.length > 0) {
        setAggregateResults({});
      }
    },
    onError() {},
  });

  useEffect(() => {
    if (columnFilters && Object?.keys(columnFilters)?.length > 0) {
      setRegisterLogsFilter((prevFilter) => ({
        ...prevFilter,
        columnFilters: { ...columnFilters },
      }));
      setRegisterStatsFilter((prevFilter) => ({
        ...prevFilter,
        columnFilters: { ...columnFilters },
      }));
    }
  }, [columnFilters]);

  useEffect(() => {
    if (sortedInfo?.column) {
      const sortBy = {
        field: sortedInfo?.columnKey,
        order: sortedInfo?.order === 'ascend' ? 'ASC' : 'DESC',
      };
      setRegisterLogsFilter((prevFilter) => ({
        ...prevFilter,
        sortBy,
      }));
    }
  }, [sortedInfo]);

  useEffect(() => {
    setRegisterLogsFilter((prevFilter) => ({
      ...prevFilter,
      search: searchValue || null,
      skip: 0,
    }));
    setRegisterStatsFilter((prevFilter) => ({
      ...prevFilter,
      search: searchValue || null,
    }));
  }, [searchValue]);

  useEffect(() => {
    setRegisterLogsFilter((prevFilter) => ({
      ...prevFilter,
      projectAgencyIds:
        globalTeamFilter?.length > 0 ? globalTeamFilter : undefined,
    }));
    setRegisterStatsFilter((prevFilter) => ({
      ...prevFilter,
      projectAgencyIds:
        globalTeamFilter?.length > 0 ? globalTeamFilter : undefined,
    }));
  }, [globalTeamFilter]);

  useEffect(() => {
    setRegisterLogsFilter((prevFilter) => ({
      ...prevFilter,
      status: approvalStatus || undefined,
      skip: 0,
    }));
  }, [approvalStatus]);

  useEffect(() => {
    if (!registerDetailsData?.id || !showListView) return;
    if (registerDetailsData !== undefined) {
      getRegisterLogs({
        variables: {
          registerId: registerDetailsData?.id,
          filter: registerLogsFilter,
        },
      });
    }
  }, [registerDetailsData, registerLogsFilter, showListView]);

  const renderAggregateDropdown = (key, fieldType, calculationResultType) => (
    <AggregateDropdown
      fieldKey={key}
      fieldType={fieldType}
      calculationResultType={calculationResultType}
      selectedAggregates={selectedAggregates}
      onAggregateChange={(fieldKey, checkedValues) =>
        handleAggregateChange(
          fieldKey,
          checkedValues,
          getRegisterStats,
          registerId,
          REGISTER_DETAILS_TYPES.DETAIL,
          registerStatsFilter,
        )
      }
      onSelectAll={(fieldKey) =>
        handleSelectAll(
          fieldKey,
          getRegisterStats,
          registerId,
          REGISTER_DETAILS_TYPES.DETAIL,
          registerStatsFilter,
        )
      }
      onClearAll={(fieldKey) =>
        handleClearAll(
          fieldKey,
          getRegisterStats,
          registerId,
          REGISTER_DETAILS_TYPES.DETAIL,
          registerStatsFilter,
        )
      }
      hasData={paginationProps?.total > 0}
    />
  );

  // Separate active and deleted registerDetails
  const activeRegisterDetails = registerDetailsData?.registerDetails?.filter(
    (data) => !data?.deletedAt,
  );
  const deletedRegisterDetails = registerDetailsData?.registerDetails?.filter(
    (data) => data?.deletedAt,
  );
  const renderEqcCell = (items) => {
    if (!Array?.isArray(items) || items?.length === 0) return '-';
    const visibleItems = items?.slice(0, MAX_EQC_DISPLAY_COUNT);
    const hiddenItems = items?.slice(MAX_EQC_DISPLAY_COUNT);
    const hasHidden = hiddenItems?.length > 0;
    const renderLink = (item, isTooltip = false) => {
      // eslint-disable-next-line no-undef
      const url = `${window?.location?.origin}/projects/${projectId}/eqc/${item?.id}`;
      const label = item?.eqcName || item?.id;
      const handleClick = (e) => {
        e?.stopPropagation();
        if (!hasInspectionAccess) {
          e?.preventDefault();
          message?.warning('Your tenant does not have inspection access');
        }
      };
      if (isTooltip) {
        return (
          <div key={item?.id} className="pb-5">
            <Tooltip title={label} placement="right">
              <a
                href={hasInspectionAccess ? url : '#'}
                target={hasInspectionAccess ? '_blank' : '_self'}
                rel="noopener noreferrer"
                onClick={handleClick}
                className="eqc-tooltip-link-item"
              >
                {label}
              </a>
            </Tooltip>
          </div>
        );
      }
      return (
        <a
          key={item?.id}
          href={hasInspectionAccess ? url : '#'}
          target={hasInspectionAccess ? '_blank' : '_self'}
          rel="noopener noreferrer"
          onClick={handleClick}
          className="eqc-label"
        >
          {label}
        </a>
      );
    };
    return (
      <div className="d-flex align-items-center flex-wrap">
        {visibleItems?.map((item, index) => {
          const showComma = index < visibleItems?.length - 1 || hasHidden;
          return (
            <React.Fragment key={item?.id}>
              <Tooltip title={item?.eqcName || item?.id}>
                {renderLink(item)}
              </Tooltip>
              {showComma && <span className="mr-5">, </span>}
            </React.Fragment>
          );
        })}
        {hasHidden && (
          <Tooltip
            classNames={{ root: 'eqc-hyperlink-tooltip' }}
            title={
              <div className="">
                {hiddenItems?.map((item) => renderLink(item, true))}
              </div>
            }
            placement="top"
          >
            <span
              className="text-secondary pointer"
              onClick={(e) => e?.stopPropagation()}
            >
              +{hiddenItems?.length} Eqc
            </span>
          </Tooltip>
        )}
      </div>
    );
  };

  const stageStatusTags = (status) => {
    switch (status) {
      case STAGE_STATUS_KEYS.APPROVED:
        return (
          <Tag className="active-tag">
            {titleCase(REGISTER_LOG_STATUS.APPROVED)}
          </Tag>
        );
      case STAGE_STATUS_KEYS.APPROVAL_PENDING:
        return (
          <Tag className="yellow-tag">
            {titleCase(REGISTER_LOG_STATUS.APPROVAL_PENDING)}
          </Tag>
        );
      case STAGE_STATUS_KEYS.REDO:
        return <Tag color="red">{titleCase(REGISTER_LOG_STATUS.REDO)}</Tag>;
      case STAGE_STATUS_KEYS.REJECTED:
        return <Tag color="red">{titleCase(REGISTER_LOG_STATUS.REJECTED)}</Tag>;
      default:
        return null;
    }
  };

  const getApprovalHistoryContent = (approvalLevels) => {
    return map(approvalLevels, (record, index) => {
      const approvers = record?.approvers || [];
      const hasApprovers = approvers?.length > 0;
      const isApprovalPending =
        record?.status === STAGE_STATUS_KEYS.APPROVAL_PENDING;

      return (
        <>
          <Card className="approvers-card">
            <Row justify="space-between" gutter={18}>
              <Col span={14}>
                <EllipsisText
                  className="text-secondary mb-15 level-name"
                  text={record?.levelName}
                />
                {!isApprovalPending && hasApprovers && (
                  <div>
                    By
                    {map(approvers, (approverName, approverIndex) => (
                      <Tag
                        key={approverIndex}
                        className="ml-5 approver-name-tag"
                      >
                        <EllipsisText text={approverName} />
                      </Tag>
                    ))}
                  </div>
                )}
              </Col>
              <Col span={10}>
                <div className="mb-15">{stageStatusTags(record?.status)}</div>
                {!isApprovalPending && (
                  <div className="font-size-12">
                    {moment(record?.createdAt)
                      ?.tz(currentProjectDetails?.timeZone)
                      ?.format(DATE_TIME_WITH_AT)}
                  </div>
                )}
              </Col>
              {isApprovalPending && hasApprovers && (
                <Col span={24}>
                  <div>
                    from
                    <>
                      <Tag className="ml-5 approver-name-tag">
                        <EllipsisText text={approvers?.[0]} />
                      </Tag>
                      {approvers?.length > 1 && (
                        <CommonTooltip
                          title={slice(approvers, 1, approvers?.length).join(
                            ', ',
                          )}
                          getPopupContainer={() =>
                            // eslint-disable-next-line no-undef
                            document.querySelector('.project-tab-details') ||
                            // eslint-disable-next-line no-undef
                            document.body
                          }
                          placement={isDesktopViewport ? 'right' : 'top'}
                        >
                          {`and +${approvers?.length - 1}`}
                        </CommonTooltip>
                      )}
                    </>
                  </div>
                </Col>
              )}
            </Row>
          </Card>
          {approvalLevels?.length - 1 > index && (
            <Divider className="approver-divider" dashed />
          )}
        </>
      );
    });
  };

  const getStatusWithApproverInfo = (value, record) => {
    if (REGISTER_LOG_STATUS.COMPLETED === value) {
      return '-';
    }
    const approvalLevels = record?.unifiedapprovalhistory || [];

    return (
      <Flex vertical justify="center">
        <div className="d-flex align-center">
          {record?.currentapprovalname && (
            <div>({record?.currentapprovalname || '-'})</div>
          )}
          {approvalLevels?.length > 0 && (
            <CommonPopover
              classNames={{ root: 'approver-info' }}
              onOpenChange={(visible) => {
                setOpenApproverPopover(visible);
              }}
              autoAdjustOverflow={!isDesktopViewport}
              getPopupContainer={() =>
                // eslint-disable-next-line no-undef
                document.querySelector('.pointer') || document.body
              }
              title={
                <Row
                  justify="space-between"
                  align="top"
                  onClick={(e) => e?.stopPropagation()}
                >
                  <Col span={20}>
                    <EllipsisText text="Approval History" />
                  </Col>
                  <Col span={2}>
                    <CloseIcon
                      height={24}
                      width={24}
                      className="pointer"
                      onClick={(e) => {
                        e?.stopPropagation();
                        setOpenApproverPopover(false);
                      }}
                    />
                  </Col>
                </Row>
              }
              trigger="click"
              placement="right"
              destroyOnHidden
              open={
                openApproverPopover && selectedApproverRecord?.id === record?.id
              }
              content={
                <div onClick={(e) => e?.stopPropagation()}>
                  {getApprovalHistoryContent(approvalLevels)}
                </div>
              }
            >
              <InfoIconDark
                height={18}
                width={18}
                className="approver-info-icon ml-5 pointer"
                onClick={(e) => {
                  e?.stopPropagation();
                  setSelectedApproverRecord(record);
                }}
              />
            </CommonPopover>
          )}
        </div>
        <div className="mt-5">
          <Tag className={clsx(REGISTER_STATUS_CLASSNAME[value], 'mr-2')}>
            {titleCase(
              REGISTER_LOG_STATUS[
                value === REGISTER_LOG_STATUS.REDO_DRAFT
                  ? REGISTER_LOG_STATUS?.REDO
                  : value
              ],
            )}
          </Tag>
          {value === REGISTER_LOG_STATUS.REDO_DRAFT && '(Draft)'}
        </div>
      </Flex>
    );
  };

  const columns = [
    {
      title: '#',
      key: 'id',
      dataIndex: 'index',
      render: (text, record, index) => (
        <div>{registerLogsFilter?.skip + index + 1}</div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      index: 'status',
      render: (value, record) => getStatusWithApproverInfo(value, record),
    },
    // Active columns first
    ...map(activeRegisterDetails, (data) => ({
      title: (
        <div>
          <div className="d-flex align-items-center">
            {data?.isRequired && <div className="required-mark">*</div>}
            <div className="mr-5">
              <FieldIcon fieldType={data?.type} />
            </div>
            <div className="mr-5">{data?.name}</div>
            {renderAggregateDropdown(
              data?.id,
              data?.type,
              data?.calculationResultType,
            )}
          </div>
          {aggregateResults?.[data?.id] && (
            <div className="aggregate-results">
              {Object?.entries(aggregateResults[data?.id])?.map(
                ([operation, result]) => (
                  <div key={operation} className="aggregate-result-item">
                    <span className="aggregate-function-name">
                      {API_TO_AGGREGATE_FUNCTION_LABELS[operation]}:
                    </span>
                    <span className="aggregate-function-value">{result}</span>
                  </div>
                ),
              )}
            </div>
          )}
        </div>
      ),
      isRequired: `${data?.isRequired}`,
      dataIndex: `${toCamelCase(data?.name)}`,
      key: data?.id,
      index: `${toCamelCase(data?.name)}`,
      className: ` ${data?.type === PROPERTY_TYPES?.NOMENCLATURE ? 'nomenclature-column' : 'min-width-150'} `,
      filteredValue: columnFilters[data?.id] || null,
      filterDropdown: (filterProps) => (
        <TableFilterDropdown
          {...filterProps}
          query={GET_REGISTER_LOG_DROPDOWN_LIST}
          variables={{ registerId, fieldId: data?.id }}
          responsePath="registerLogDetailDistinctValues"
          projectAgencyIds={globalTeamFilter}
          fieldType={data?.type}
        />
      ),
      filterDropdownProps: { placement: 'bottomLeft' },
      sorter: true,
      sortOrder: sortedInfo?.columnKey === `${data?.id}` && sortedInfo?.order,
      render: (text) => {
        if (data?.type === PROPERTY_TYPES?.NOMENCLATURE) {
          return <NomenclatureItem text={text} />;
        }
        if (data?.type === PROPERTY_TYPES?.EQC) {
          return <div className="eqc-field">{renderEqcCell(text)}</div>;
        }
        if (Array?.isArray(text)) {
          return (
            <div>
              {text
                ?.map((item) => item?.name || item?.id || item)
                ?.join(', ') || '-'}
            </div>
          );
        }

        if (moment(text, DATE_ISO_FORMAT, true).isValid()) {
          return moment(text)?.format(DEFAULTDATEFORMAT);
        }
        if (moment(text, TIME_24_HOUR_FORMAT, true)?.isValid()) {
          return moment(text, TIME_24_HOUR_FORMAT).format(TIME_12_HOUR_FORMAT);
        }
        return (
          <div>
            <EllipsisText text={text || '-'} heightRows={3} />
          </div>
        );
      },
    })),
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      index: 'createdBy',
      className: 'min-width-150',
      filteredValue: columnFilters?.createdBy || null,
      filterDropdown: (filterProps) => (
        <TableFilterDropdown
          {...filterProps}
          query={GET_REGISTER_LOG_DROPDOWN_LIST}
          variables={{ registerId, fieldId: 'createdBy' }}
          responsePath="registerLogDetailDistinctValues"
          labelPath="label"
          projectAgencyIds={globalTeamFilter}
        />
      ),
      filterDropdownProps: { placement: 'bottomLeft' },
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'createdBy' && sortedInfo?.order,
    },
    {
      title: 'Created On',
      dataIndex: 'createdAt',
      key: 'createdAt',
      index: 'createdAt',
      className: 'min-width-150',
      filteredValue: columnFilters?.createdAt || null,
      filterDropdown: (filterProps) => (
        <TableFilterDropdown
          {...filterProps}
          query={GET_REGISTER_LOG_DROPDOWN_LIST}
          variables={{ registerId, fieldId: 'createdAt' }}
          responsePath="registerLogDetailDistinctValues"
          projectAgencyIds={globalTeamFilter}
          fieldType={PROPERTY_TYPES?.DATE}
        />
      ),
      filterDropdownProps: { placement: 'bottomLeft' },
      sorter: true,
      sortOrder: sortedInfo?.columnKey === 'createdAt' && sortedInfo?.order,
    },

    // Deleted columns at the end - only show if showDeletedEntries is true
    ...(showDeletedEntries
      ? map(deletedRegisterDetails, (data) => ({
          title: (
            <div className="deleted-field">
              <div className="d-flex align-items-center">
                {data?.isRequired && <div className="required-mark">*</div>}
                <div className="mr-5">
                  <FieldIcon fieldType={data?.type} />
                </div>
                <div className="mr-5">{data?.name} (Deleted)</div>
                {renderAggregateDropdown(data?.id, data?.type)}
              </div>
              {aggregateResults?.[data?.id] && (
                <div className="aggregate-results">
                  {Object?.entries(aggregateResults[data?.id])?.map(
                    ([operation, result]) => (
                      <div key={operation} className="aggregate-result-item">
                        <span className="aggregate-function-name">
                          {API_TO_AGGREGATE_FUNCTION_LABELS[operation]}:
                        </span>
                        <span className="aggregate-function-value">
                          {result}
                        </span>
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>
          ),
          isRequired: `${data?.isRequired}`,
          dataIndex: `${toCamelCase(data?.name)}`,
          index: `${toCamelCase(data?.name)}`,
          className: 'min-width-150 deleted-field-column',
          render: (text) => {
            if (data?.type === PROPERTY_TYPES?.NOMENCLATURE) {
              return (
                <div className="deleted-field">
                  <NomenclatureItem text={text} />
                </div>
              );
            }
            if (data?.type === PROPERTY_TYPES?.EQC) {
              return (
                <div className="eqc-field deleted-field">
                  {renderEqcCell(text)}
                </div>
              );
            }
            if (moment(text, DATE_ISO_FORMAT, true).isValid()) {
              return (
                <div className="deleted-field">
                  {moment(text)?.format(DEFAULTDATEFORMAT)}
                </div>
              );
            }
            if (moment(text, TIME_24_HOUR_FORMAT, true)?.isValid()) {
              return (
                <div className="deleted-field">
                  {moment(text, TIME_24_HOUR_FORMAT).format(
                    TIME_12_HOUR_FORMAT,
                  )}
                </div>
              );
            }
            return <div className="deleted-field">{text || '-'}</div>;
          },
        }))
      : []),
  ];

  const handleTableChange = (pagination, filters, sorter) => {
    const { current } = pagination;
    const skip = (current - 1) * pagination?.pageSize;
    setPaginationProps({ ...paginationProps, ...pagination });

    const wasSorterCleared = sortedInfo?.column && !sorter?.column;

    setSortedInfo(sorter);

    let sortBy;
    if (sorter?.column) {
      sortBy = {
        field: sorter?.columnKey,
        order: sorter?.order === 'ascend' ? 'ASC' : 'DESC',
      };
    } else if (wasSorterCleared) {
      sortBy = undefined;
    } else {
      sortBy = registerLogsFilter?.sortBy;
    }

    const filteredColumnFilters = Object?.fromEntries(
      Object?.entries(filters || {})?.filter(
        ([, value]) => value !== null && value !== undefined && value?.length > 0,
      ),
    );
    const hasActiveFilters = Object?.keys(filteredColumnFilters)?.length > 0;
    const newColumnFilters = hasActiveFilters ? filteredColumnFilters : undefined;

    if (filters) {
      setColumnFilters(hasActiveFilters ? filteredColumnFilters : {});
    }

    if (!hasActiveFilters && registerLogsFilter?.columnFilters) {
      removeHistoryStateData(navigate, location, 'registerColumnFilter');
    }

    if (wasSorterCleared) {
      removeHistoryStateData(navigate, location, 'registerLogSortedInfo');
    }

    const newFilter = {
      ...registerLogsFilter,
      skip,
      limit: pagination?.pageSize,
      sortBy,
      columnFilters: newColumnFilters,
    };

    setRegisterLogsFilter(newFilter);
    setRegisterStatsFilter((prevFilter) => ({
      ...prevFilter,
      columnFilters: newColumnFilters,
    }));

    getRegisterLogs({
      variables: {
        registerId: registerDetailsData?.id,
        filter: newFilter,
      },
    });
  };

  const handleRowClick = (record) => {
    const registerLogId = record?.id;
    navigate(
      `${ROUTES.PROJECTS}/${projectId}/${TAB_KEYS.REGISTER}/${registerId}/${registerLogId}`,
      {
        state: {
          registerDetailsData,
          currentPath: location?.pathname,
          showListView,
          registerNavFilter,
          registerId,
          registerDetailsSearchValue: searchValue,
          registerLogColumnFilter: registerLogsFilter?.columnFilters,
          registerLogSortedInfo: registerLogsFilter?.sortBy,
        },
      },
    );
  };

  const handleRefetch = () => {
    if (!loading && hasMore) {
      setScrollFlag(true);
      getRegisterLogs({
        variables: {
          registerId: registerDetailsData?.id,
          filter: {
            ...registerLogsFilter,
            skip: setsData?.length,
            limit: DEFAULT_PAGE_SIZE,
          },
        },
      });
    }
  };

  const renderMobileValue = (value, fieldType) => {
    if (
      fieldType === PROPERTY_TYPES?.EQC &&
      Array?.isArray(value) &&
      value?.length > 0
    ) {
      return renderEqcCell(value);
    }
    if (Array?.isArray(value)) {
      return value?.map((item) => item?.name || item?.id || item)?.join(', ');
    }
    if (moment(value, DATE_ISO_FORMAT, true)?.isValid()) {
      return moment(value)?.format(DEFAULTDATEFORMAT);
    }
    if (moment(value, TIME_24_HOUR_FORMAT, true)?.isValid()) {
      return moment(value, TIME_24_HOUR_FORMAT)?.format(TIME_12_HOUR_FORMAT);
    }
    return value || '-';
  };
  return (
    <div className="pointer">
      {isDesktopViewport ? (
        <CommonTable
          data={setsData || []}
          columns={columns}
          loading={loading}
          onChange={handleTableChange}
          paginationConfig={paginationProps}
          id="register-sets-desktop-table"
          onRow={(record) => {
            return {
              onClick: () => handleRowClick(record),
            };
          }}
        />
      ) : (
        <InfiniteScrollHandler
          scrollFlag={scrollFlag}
          setScrollFlag={setScrollFlag}
          hasMore={hasMore}
          loading={loading}
          refetchData={handleRefetch}
          dataLength={setsData?.length}
          skeletonRows={columns?.length - 2}
        >
          <div>
            {map(setsData, (data, index) => {
              return (
                <CommonCard
                  key={index}
                  onClick={() => handleRowClick(data)}
                  id={`register-set-card-${data?.id || index}`}
                >
                  <div className="common-card d-flex">
                    <div className="mr-5 fw-medium">
                      {registerLogsFilter?.skip + index + 1}.
                    </div>
                    <div>
                      <div className="card-content text-secondary">
                        {/* Status */}
                        <div className="mb-15 d-flex align-center">
                          <span className="fw-medium mr-5">Status:</span>
                          <span>
                            {getStatusWithApproverInfo(data?.status, data)}
                          </span>
                        </div>
                        {/* Active register detail fields */}
                        {map(activeRegisterDetails, (fieldDetail) => {
                          const key = toCamelCase(fieldDetail?.name);
                          const value = data?.[key];
                          return (
                            <div
                              className="mb-15 d-flex align-center"
                              key={key}
                            >
                              <span className="fw-medium mr-5">
                                {fieldDetail?.isRequired && (
                                  <span className="required-mark">*</span>
                                )}
                                {fieldDetail?.name}:
                              </span>
                              <span>
                                {renderMobileValue(value, fieldDetail?.type)}
                              </span>
                            </div>
                          );
                        })}
                        {/* Created By */}
                        <div className="mb-15 d-flex align-center">
                          <span className="fw-medium mr-5">Created By:</span>
                          <span>{data?.createdBy || '-'}</span>
                        </div>
                        {/* Created On */}
                        <div className="mb-15 d-flex align-center">
                          <span className="fw-medium mr-5">Created On:</span>
                          <span>{data?.createdAt || '-'}</span>
                        </div>
                        {/* Deleted register detail fields - only show if showDeletedEntries is true */}
                        {showDeletedEntries &&
                          map(deletedRegisterDetails, (fieldDetail) => {
                            const key = toCamelCase(fieldDetail?.name);
                            const value = data?.[key];
                            return (
                              <div
                                className="mb-15 d-flex align-center deleted-field"
                                key={key}
                              >
                                <span className="fw-medium mr-5">
                                  {fieldDetail?.isRequired && (
                                    <span className="required-mark">*</span>
                                  )}
                                  {fieldDetail?.name} (Deleted):
                                </span>
                                <span>
                                  {renderMobileValue(value, fieldDetail?.type)}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  </div>
                </CommonCard>
              );
            })}
          </div>
        </InfiniteScrollHandler>
      )}
    </div>
  );
};

export default SetsTable;
